import React, { useState } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { useTheme } from '@mui/material/styles'
import { Skeleton } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import ShowIcon from '@mui/icons-material/VisibilityOutlined'
import HideIcon from '@mui/icons-material/VisibilityOffOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import BellIcon from '@mui/icons-material/Notifications'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { FULL_DATETIME_FORMAT, DAY_IN_US_FORMAT } from '@/v2/constants'
import { getDateTimeWithoutTimezone, safeFormat } from '@/v2/utils/convert'
import { parseHtmlToString } from '@/v2/utils/helpers'
import useModals from '@/v2/hooks/useModals'
import { useProfileNotes } from '@/v2/hooks/useProfileNotes'
import DraftInsuranceCards from '@/v2/containers/patient-insurance/mobile/DraftInsuranceCards'
import FamilyMembersMobile from './FamilyMembers'

const Wrapper = styled.div`
  padding: 16px;
`

const MedicalAlertsSection = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
`

const MedicalAlertsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const MedicalAlertsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const NextAppointmentSection = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;
`

const ChangeLink = styled.a`
  color: ${({ theme }) => theme?.primary?.main || '#1976d2'};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`

const InfoRow = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
`

const InfoItem = styled.div`
  flex: 1;
`

const Label = styled(Typography)`
  color: ${({ theme }) => theme?.gray?.dark || '#666'};
  margin-bottom: 4px;
`

const Value = styled(Typography)`
  font-weight: 600;
`

const SSNWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const InsuranceSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`

const OfficeNotesSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`

const OfficeNotesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const OfficeNotesToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const OfficeNoteCard = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  background: #e7f2cf;
  border-radius: 12px;
  opacity: ${({ $alerts }) => ($alerts ? '1' : '0.7')};
`

const OfficeNoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const OfficeNoteContent = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: #333;
  margin-bottom: 8px;
`

const OfficeNoteDate = styled.div`
  font-size: 12px;
  color: #666;
`

const OfficeNoteAuthor = styled.div`
  font-size: 12px;
  color: #666;
`

const OfficeNoteActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ProfileMobile = () => {
  const theme = useTheme()
  const { id: patientId } = useParams()
  const {
    patient,
    medicalAlerts,
    officeNotes,
    onSetPatient,
    onSetOfficeNotes,
  } = usePatientContext()
  const { goTo } = useModals()
  const { deleteProfileNote, updateProfileNote } = useProfileNotes()
  const [showSSN, setShowSSN] = useState(false)
  const [officeNotesExpanded, setOfficeNotesExpanded] = useState(true)
  const [noteMenuAnchor, setNoteMenuAnchor] = useState(null)
  const [selectedNote, setSelectedNote] = useState(null)

  if (!patient?.id) {
    return (
      <Wrapper>
        <Section>
          <Skeleton variant="text" width={200} height={24} />
          <Skeleton variant="text" width={150} height={20} />
        </Section>
        <Section>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={120} height={20} />
        </Section>
      </Wrapper>
    )
  }

  const nextAppointment = patient.nextAppointments?.[0]

  const handleChangeAppointment = () => {
    if (nextAppointment?.id) {
      goTo(`/v2/appointment/${nextAppointment.id}/edit`)
    }
  }

  // Office note handlers
  const handleNoteMenuOpen = (event, note) => {
    event.stopPropagation()
    setNoteMenuAnchor(event.currentTarget)
    setSelectedNote(note)
  }

  const handleNoteMenuClose = () => {
    setNoteMenuAnchor(null)
    setSelectedNote(null)
  }

  const handleEditNote = () => {
    if (selectedNote) {
      goTo(`/v2/patient/${patientId}/notes/${selectedNote.id}`, {
        note: selectedNote,
        onSave: updatedNote => {
          onSetOfficeNotes(
            officeNotes.map(note =>
              note.id === updatedNote.id ? updatedNote : note
            )
          )
        },
      })
    }
    handleNoteMenuClose()
  }

  const handleDeleteNote = async () => {
    if (selectedNote) {
      await deleteProfileNote({
        note: {
          id: selectedNote.id,
          patientId,
        },
      })
    }
    handleNoteMenuClose()
  }

  const handleToggleAlert = async note => {
    await updateProfileNote({
      note: {
        id: note.id,
        annotation: note.annotation,
        alerts: !note.alerts,
        patientId,
      },
    })
  }

  const handleAddOfficeNote = () => {
    goTo(`/v2/patient/${patientId}/notes/new/green`, {
      onSave: () => {
        setOfficeNotesExpanded(true)
      },
    })
  }

  return (
    <Wrapper>
      {medicalAlerts.length > 0 && (
        <MedicalAlertsSection>
          <MedicalAlertsHeader>
            <Typography variant="h4" fontSize="14px" fontWeight="600">
              Medical Alerts ({medicalAlerts.length})
            </Typography>
            <Button
              variant="text"
              onClick={() =>
                goTo(`/v2/patient/${patient.id}/medical-alerts`, {
                  patientId: patient.id,
                  value: medicalAlerts,
                  onSuccess: data => {
                    onSetPatient(data)
                  },
                })
              }
              sx={{
                color: theme.palette.gray.dark,
                padding: 0,
                minWidth: 'auto',
              }}
            >
              <Typography variant="h5" fontSize="12px">
                Manage
              </Typography>
            </Button>
          </MedicalAlertsHeader>
          <MedicalAlertsList>
            {medicalAlerts.map(({ name, label, color }) => (
              <Button
                key={name}
                variant="contained"
                sx={{
                  background: theme.palette[color].medicalAlert,
                  color: theme.palette.background.default,
                  fontSize: '12px',
                  padding: '4px 12px',
                  minWidth: 'auto',
                }}
              >
                {label}
              </Button>
            ))}
          </MedicalAlertsList>
        </MedicalAlertsSection>
      )}

      {nextAppointment && (
        <NextAppointmentSection>
          <Typography
            variant="h4"
            fontSize="14px"
            fontWeight="600"
            marginBottom="4px"
          >
            Next Appointment:{' '}
            <Typography as="span" fontWeight="400">
              {nextAppointment.appointmentType?.title || 'Appointment'}
            </Typography>
          </Typography>
          <Typography variant="h5" fontSize="14px" color="textSecondary">
            {safeFormat(
              new Date(getDateTimeWithoutTimezone(nextAppointment.startsAt)),
              FULL_DATETIME_FORMAT
            )}
          </Typography>
          <ChangeLink theme={theme.palette} onClick={handleChangeAppointment}>
            Change APPOINTMENT
          </ChangeLink>
        </NextAppointmentSection>
      )}

      <InfoRow>
        <InfoItem>
          <Label variant="h6" fontSize="12px" theme={theme.palette}>
            Date of birth
          </Label>
          <Value variant="h5" fontSize="14px">
            {patient.dob
              ? format(parseISO(patient.dob), DAY_IN_US_FORMAT)
              : 'Not informed'}
          </Value>
        </InfoItem>
        <InfoItem>
          <Label variant="h6" fontSize="12px" theme={theme.palette}>
            Gender
          </Label>
          <Value variant="h5" fontSize="14px">
            {patient.gender
              ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)
              : 'Not informed'}
          </Value>
        </InfoItem>
      </InfoRow>

      <Section>
        <Label variant="h6" fontSize="12px" theme={theme.palette}>
          SSN
        </Label>
        <SSNWrapper>
          <Value variant="h5" fontSize="14px">
            {showSSN ? patient.ssn || 'Not informed' : '**************'}
          </Value>
          <IconButton
            size="small"
            onClick={() => setShowSSN(!showSSN)}
            sx={{ padding: '4px' }}
          >
            {showSSN ? (
              <HideIcon
                fontSize="small"
                sx={{ color: theme.palette.gray?.dark || '#666' }}
              />
            ) : (
              <ShowIcon
                fontSize="small"
                sx={{ color: theme.palette.gray?.dark || '#666' }}
              />
            )}
          </IconButton>
        </SSNWrapper>
      </Section>

      <Section>
        <Label variant="h6" fontSize="12px" theme={theme.palette}>
          Heard about us
        </Label>
        <Value variant="h5" fontSize="14px">
          {patient.referredBy || 'Not informed'}
        </Value>
      </Section>

      <Section>
        <Label variant="h6" fontSize="12px" theme={theme.palette}>
          Lives, works, or studies in MA
        </Label>
        <Value variant="h5" fontSize="14px">
          {patient.maAttestedAt
            ? `Confirmed on ${safeFormat(
                patient.maAttestedAt,
                DAY_IN_US_FORMAT
              )}`
            : 'Not confirmed'}
        </Value>
      </Section>

      {patient?.insuranceForms?.length > 0 && (
        <InsuranceSection>
          <DraftInsuranceCards />
        </InsuranceSection>
      )}

      {/* Office Notes Section */}
      <OfficeNotesSection>
        <OfficeNotesHeader>
          <OfficeNotesToggle
            onClick={() => setOfficeNotesExpanded(!officeNotesExpanded)}
          >
            <Typography variant="h4" fontSize="14px" fontWeight="600">
              Office Notes ({officeNotes?.length || 0})
            </Typography>
            {officeNotes?.length > 0 &&
              (officeNotesExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              ))}
          </OfficeNotesToggle>
          <Button
            variant="text"
            onClick={handleAddOfficeNote}
            sx={{
              color: theme.palette.gray?.dark || '#666',
              padding: 0,
              minWidth: 'auto',
            }}
          >
            <Typography variant="h5" fontSize="12px">
              Add note
            </Typography>
          </Button>
        </OfficeNotesHeader>

        {officeNotes?.length === 0 && (
          <Typography
            variant="h5"
            fontSize="14px"
            color="textSecondary"
            textAlign="center"
          >
            No notes
          </Typography>
        )}

        {officeNotesExpanded && officeNotes?.length > 0 && (
          <>
            {officeNotes.map(note => {
              const noteDate = getDateTimeWithoutTimezone(note.createdAt)
              return (
                <OfficeNoteCard key={note.id} $alerts={note.alerts ? 1 : 0}>
                  <OfficeNoteHeader>
                    <div style={{ flex: 1 }}>
                      <OfficeNoteContent>
                        {parseHtmlToString(note.annotation)}
                      </OfficeNoteContent>
                      <OfficeNoteDate>
                        {safeFormat(noteDate, 'MMMM dd, yyyy')}
                      </OfficeNoteDate>
                      <OfficeNoteAuthor>
                        Dr. {note.user?.fullName || 'Not Assigned'}
                      </OfficeNoteAuthor>
                    </div>
                    <OfficeNoteActions>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleAlert(note)}
                        sx={{ padding: '4px' }}
                      >
                        <BellIcon
                          fontSize="small"
                          sx={{ opacity: note.alerts ? '1' : '0.5' }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={e => handleNoteMenuOpen(e, note)}
                        sx={{ padding: '4px' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </OfficeNoteActions>
                  </OfficeNoteHeader>
                </OfficeNoteCard>
              )
            })}
          </>
        )}
      </OfficeNotesSection>

      <FamilyMembersMobile />

      {/* Office Note Menu */}
      <Menu
        anchorEl={noteMenuAnchor}
        open={Boolean(noteMenuAnchor)}
        onClose={handleNoteMenuClose}
      >
        <MenuItem onClick={handleEditNote}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteNote}>Remove</MenuItem>
      </Menu>
    </Wrapper>
  )
}

export default ProfileMobile
